import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import SubscriptionForm from '../components/SubscriptionForm';

const COUNTRY_CODES = [
  { code: '+1', label: '+1 (US/CA)' },
  { code: '+91', label: '+91 (India)' },
  { code: '+44', label: '+44 (UK)' },
  { code: '+61', label: '+61 (Australia)' },
  { code: '+49', label: '+49 (Germany)' },
  { code: '+33', label: '+33 (France)' },
  { code: '+81', label: '+81 (Japan)' },
  { code: '+86', label: '+86 (China)' },
  { code: '+55', label: '+55 (Brazil)' },
  { code: '+27', label: '+27 (South Africa)' },
];

export default function AccountPage() {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [subscribeNews, setSubscribeNews] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  // Check for session expired message on mount
  useEffect(() => {
    const sessionMsg = localStorage.getItem('vedanta_session_message');
    if (sessionMsg) {
      setMessage(sessionMsg);
      localStorage.removeItem('vedanta_session_message');
    }
  }, []);

  if (user) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2>Welcome, {user.full_name}</h2>
          <p>{user.email}</p>
          {user.roles?.includes('admin') && (
            <p><a href="/admin">Go to Admin Panel</a></p>
          )}
          
          <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)' }}>
            <SubscriptionForm 
              preloadedEmail={user.email} 
              preloadedName={user.full_name} 
              isLoggedIn={true} 
            />
          </div>
          
          <button type="button" onClick={() => { logout(); navigate('/'); }}>
            Logout
          </button>
        </div>
      </div>
    );
  }

  const switchMode = (next) => { setMode(next); setError(''); setMessage(''); };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loggingIn) return;
    setError('');
    setMessage('');
    setLoggingIn(true);
    try {
      const res = await api.post('/api/auth/login', new URLSearchParams({ username: email, password }));
      if (res.data.requires_2fa) {
        // Admin user: store memberId and redirect to 2FA verification
        localStorage.setItem('vedanta_pending_2fa_member_id', res.data.member_id);
        navigate('/admin-verify');
      } else {
        await login(res.data.access_token);
        navigate('/');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid credentials. Please try again.');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      await api.post('/api/auth/signup', {
        full_name: fullName,
        email,
        password,
        phone: phone || undefined,
        country_code: countryCode,
        subscribe_news: subscribeNews,
      });
      setMessage('Account created! Check your email to verify before logging in.');
      switchMode('login');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Signup failed. Please try again.');
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      await api.post('/api/auth/request-reset', { email });
    } catch {
      // fall through — always show same message
    }
    setMessage('If an account exists for that email, a reset link has been sent.');
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        {mode !== 'forgot' && (
          <div className="auth-toggle" aria-label="Account mode selector">
            <button
              type="button"
              className={`auth-tab ${mode === 'login' ? 'active' : ''}`}
              onClick={() => switchMode('login')}
            >
              Login
            </button>
            <button
              type="button"
              className={`auth-tab ${mode === 'signup' ? 'active' : ''}`}
              onClick={() => switchMode('signup')}
            >
              Signup
            </button>
          </div>
        )}

        {mode === 'login' && (
          <form onSubmit={handleLogin} className="auth-form">
            <h2>Welcome back</h2>
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label>
              Password
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
            <button type="submit" disabled={loggingIn}>
              {loggingIn ? 'Logging in...' : 'Login'}
            </button>
            <button type="button" className="auth-link-btn" onClick={() => switchMode('forgot')}>
              Forgot password?
            </button>
            {error && <p className="form-error">{error}</p>}
            {message && <p className="form-message">{message}</p>}
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleSignup} className="auth-form">
            <h2>Create account</h2>
            <label>
              Full Name
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
            </label>
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label>
              Password (min 8 characters)
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} maxLength={18} />
            </label>
            <label>Phone (optional)</label>
            <div className="phone-row">
              <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)} aria-label="Country code">
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
                aria-label="Phone number"
              />
            </div>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={subscribeNews}
                onChange={(e) => setSubscribeNews(e.target.checked)}
              />
              Subscribe to newsletter &amp; events
            </label>
            <button type="submit">Create Account</button>
            {error && <p className="form-error">{error}</p>}
            {message && <p className="form-message">{message}</p>}
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleForgot} className="auth-form">
            <h2>Reset password</h2>
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <button type="submit">Send reset link</button>
            <button type="button" className="auth-link-btn" onClick={() => switchMode('login')}>
              Back to login
            </button>
            {message && <p className="form-message">{message}</p>}
          </form>
        )}
      </div>
    </div>
  );
}
